import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { centerDateKey } from '../utils/geolocation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ChevronRight,
    Building2,
    CalendarDays,
    GraduationCap,
    RefreshCw,
    ShieldAlert,
    Users,
    Eye,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import PageContainer from '../components/PageContainer';
import { greeting } from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import HomeworkAlertBoard from '../components/dashboard/HomeworkAlertBoard';
import ManualAlertBoard from '../components/dashboard/ManualAlertBoard';
import { usePermissions } from '../hooks/usePermissions';
import { attendanceApi, classesApi, dashboardApi } from '../services/api';
import {
    Button,
    EmptyState,
    ErrorState,
    LoadingState,
    LocalPagination,
    ResponsiveDataView,
    SectionPanel,
    StatCard,
    StatusBadge,
    useLocalPagination,
} from '../components/ui';
import { cn } from '../utils/cn';
import { openOnRowClick } from '../utils/rowClick';

type DashboardStat = {
    label: string;
    value: ReactNode;
    detail?: ReactNode;
    icon: LucideIcon;
    tone: 'academy' | 'action' | 'warning' | 'danger';
    path?: string;
};

type DashboardAttendanceAlerts = {
    missingClasses: DashboardAttendanceAlertItem[];
    noStudentAttendanceClasses: DashboardAttendanceAlertItem[];
};

type DashboardAttendanceAlertItem = {
    id: string;
    className: string;
    subject: string;
    campus: string;
    timeSlot: string;
    room: string;
    href: string;
};

type DashboardAttendanceAlertKind = keyof DashboardAttendanceAlerts;

const attendanceAlertLabels: Record<DashboardAttendanceAlertKind, string> = {
    missingClasses: 'Chưa tạo buổi học',
    noStudentAttendanceClasses: 'Chưa điểm danh học sinh',
};

const ATTENDANCE_PAGE_SIZE = 10;

// The three work lists of the dashboard, side by side like the priority tabs on the notifications page.
const DASHBOARD_TABS = [
    { id: 'attendance', label: 'Điểm danh hôm nay' },
    { id: 'homework', label: 'Bài tập về nhà' },
    { id: 'manual', label: 'Cảnh báo cần theo dõi' },
] as const;
type DashboardTab = typeof DASHBOARD_TABS[number]['id'];

function isDashboardTab(value: string | null): value is DashboardTab {
    return DASHBOARD_TABS.some(tab => tab.id === value);
}

function DashboardTabs({ active, counts, onChange }: {
    active: DashboardTab;
    counts: Record<DashboardTab, number | null>;
    onChange: (tab: DashboardTab) => void;
}) {
    return (
        <div role="tablist" aria-label="Việc cần xử lý" className="flex gap-1 overflow-x-auto border-b border-ink-200 px-2 sm:px-3">
            {DASHBOARD_TABS.map(tab => {
                const selected = tab.id === active;
                const count = counts[tab.id];
                return (
                    <button
                        key={tab.id}
                        id={`dashboard-tab-${tab.id}`}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        aria-controls={`dashboard-panel-${tab.id}`}
                        onClick={() => onChange(tab.id)}
                        className={cn(
                            '-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm transition-colors focus:outline-none focus-visible:bg-academy-50',
                            selected ? 'border-academy-600 font-semibold text-academy-800' : 'border-transparent text-ink-600 hover:text-ink-900',
                        )}
                    >
                        {tab.label}
                        {count ? (
                            <span className="min-w-5 rounded-full bg-danger-600 px-1.5 text-center text-[11px] font-semibold leading-5 tabular-nums text-white" aria-label={`${count} cần xử lý`}>
                                {count > 99 ? '99+' : count}
                            </span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}

type AttendanceAttentionRow = DashboardAttendanceAlertItem & { kind: DashboardAttendanceAlertKind };

// Classes that started without a session or a roll call, earliest first; a row opens the class on the attendance tab.
function AttendanceAttentionList({ alerts }: { alerts: DashboardAttendanceAlerts }) {
    const navigate = useNavigate();
    const rows: AttendanceAttentionRow[] = [
        ...alerts.missingClasses.map(item => ({ ...item, kind: 'missingClasses' as const })),
        ...alerts.noStudentAttendanceClasses.map(item => ({ ...item, kind: 'noStudentAttendanceClasses' as const })),
    ].sort((a, b) => a.timeSlot.localeCompare(b.timeSlot) || a.className.localeCompare(b.className, 'vi'));
    const { page, pageSize, pagedItems, setPage, setPageSize } = useLocalPagination(rows, ATTENDANCE_PAGE_SIZE);

    if (!rows.length) {
        return <EmptyState title="Không có lớp cần chú ý" description="Lớp đã đến giờ mà chưa tạo buổi học hoặc chưa điểm danh sẽ hiện ở đây." className="min-h-36" />;
    }
    const header = 'px-4 py-3 text-left text-xs font-medium text-ink-500';
    const kindBadge = (kind: DashboardAttendanceAlertKind) => (
        <StatusBadge tone={kind === 'missingClasses' ? 'danger' : 'warning'}>{attendanceAlertLabels[kind]}</StatusBadge>
    );
    const open = (row: AttendanceAttentionRow) => openOnRowClick(() => navigate(row.href));

    return (
        <div className="space-y-3">
            <div className="overflow-hidden rounded-lg border border-ink-200 max-lg:border-0">
                <ResponsiveDataView
                    desktopLabel="Lớp cần chú ý"
                    mobileLabel="Lớp cần chú ý (di động)"
                    desktop={(
                        <table className="min-w-full divide-y divide-ink-200 text-sm">
                            <thead className="bg-ink-50">
                                <tr>
                                    <th className={header}>Lớp</th>
                                    <th className={header}>Giờ học</th>
                                    <th className={header}>Cơ sở</th>
                                    <th className={header}>Phòng</th>
                                    <th className={header}>Vấn đề</th>
                                    <th className={header}><span className="sr-only">Mở</span></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-ink-100 bg-white">
                                {pagedItems.map(row => (
                                    <tr key={`${row.kind}-${row.id}`} aria-label={`Mở điểm danh lớp ${row.className}`} onClick={open(row)} className="cursor-pointer hover:bg-ink-100/60">
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-ink-900">{row.className}</p>
                                            <p className="text-xs text-ink-500">{row.subject}</p>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 tabular-nums text-ink-700">{row.timeSlot}</td>
                                        <td className="px-4 py-3 text-ink-700">{row.campus}</td>
                                        <td className="px-4 py-3 text-ink-700">{row.room}</td>
                                        <td className="px-4 py-3">{kindBadge(row.kind)}</td>
                                        <td className="px-2 py-3 text-ink-400"><ChevronRight className="h-4 w-4" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    mobile={pagedItems.map(row => (
                        <article key={`${row.kind}-${row.id}`} onClick={open(row)} className="cursor-pointer rounded-lg border border-ink-200 bg-white p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="font-semibold text-ink-900">{row.className}</p>
                                    <p className="mt-1 text-xs text-ink-500">{row.subject} · {row.timeSlot} · {row.campus} · Phòng {row.room}</p>
                                </div>
                                {kindBadge(row.kind)}
                            </div>
                        </article>
                    ))}
                />
            </div>
            <LocalPagination
                label="lớp cần chú ý"
                page={page}
                pageSize={pageSize}
                totalItems={rows.length}
                pageSizeOptions={[10, 20, 50, 100]}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                className="border-t-0 px-0"
            />
        </div>
    );
}

const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Ngày theo giờ Việt Nam; buổi học lưu ở 00:00 UTC nên vẫn ra đúng ngày.
function dateKey(date: Date) {
    return centerDateKey(date);
}

function dateOnly(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseClassDate(value: unknown) {
    if (!value) return null;
    const parsed = new Date(String(value));
    if (Number.isNaN(parsed.getTime())) return null;
    return dateOnly(parsed);
}

function isClassActiveOnDate(classItem: any, targetDate: Date) {
    const targetDateOnly = dateOnly(targetDate);
    const classStartDate = parseClassDate(classItem.startDate);
    const classEndDate = parseClassDate(classItem.endDate);

    if (classStartDate && targetDateOnly < classStartDate) return false;
    if (classEndDate && targetDateOnly > classEndDate) return false;
    return true;
}

function getScheduleSlot(classItem: any, targetDate: Date) {
    const dayName = dayNames[targetDate.getDay()];
    try {
        const schedule = JSON.parse(classItem.schedule || '{}');
        const slot = schedule?.[dayName];
        return typeof slot === 'string' ? slot : '';
    } catch {
        return '';
    }
}

function getStartMinutes(timeSlot: string) {
    const [startTime] = timeSlot.split('-');
    const [hours, minutes] = (startTime || '').split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return hours * 60 + minutes;
}

function classCampusName(classItem: any) {
    if (classItem.campus && typeof classItem.campus === 'object' && classItem.campus.name) {
        return classItem.campus.name;
    }
    if (typeof classItem.campus === 'string') return classItem.campus;
    return 'Chưa xác định cơ sở';
}

function classSubjectName(classItem: any) {
    if (classItem.subject && typeof classItem.subject === 'object' && classItem.subject.name) {
        return classItem.subject.name;
    }
    if (typeof classItem.subject === 'string') return classItem.subject;
    return 'Chưa xác định môn học';
}

function buildDashboardAttendanceAlerts(
    classes: any[],
    attendanceRows: any[],
    today = new Date(),
): DashboardAttendanceAlerts {
    const todayKey = dateKey(today);
    const currentMinutes = today.getHours() * 60 + today.getMinutes();
    const todayAttendanceRows = attendanceRows.filter((item) => (
        !item.date || dateKey(new Date(item.date)) === todayKey
    ));
    const attendanceClassIds = new Set(
        todayAttendanceRows
            .filter((item) => item.id !== null && item.id !== undefined)
            .map((item) => String(item.classId)),
    );
    const classInfoById = new Map(classes.map((classItem) => [String(classItem.id), classItem]));

    const missingClasses = classes
        .filter((classItem) => classItem.isActive !== false && classItem.status !== 'completed')
        .filter((classItem) => isClassActiveOnDate(classItem, today))
        .map((classItem) => ({ classItem, timeSlot: getScheduleSlot(classItem, today) }))
        .filter(({ timeSlot }) => Boolean(timeSlot))
        .filter(({ classItem }) => !attendanceClassIds.has(String(classItem.id)))
        .filter(({ timeSlot }) => {
            const startMinutes = getStartMinutes(timeSlot);
            return startMinutes === null ? false : currentMinutes > startMinutes;
        })
        .map(({ classItem, timeSlot }) => ({
            id: String(classItem.id),
            className: classItem.name || 'Lớp học',
            subject: classSubjectName(classItem),
            campus: classCampusName(classItem),
            timeSlot,
            room: classItem.room || 'Chưa xác định',
            href: `/attendance?date=${todayKey}&classId=${encodeURIComponent(String(classItem.id))}`,
        }));

    const noStudentAttendanceClasses = todayAttendanceRows
        .filter((attendance) => attendance.id !== null && attendance.id !== undefined)
        .filter((attendance) => {
            const markedCount = (attendance.presentCount || 0)
                + (attendance.absentCount || 0)
                + (attendance.lateCount || 0)
                + (attendance.excusedCount || 0);
            return markedCount === 0;
        })
        .map((attendance) => {
            const classInfo = classInfoById.get(String(attendance.classId)) || attendance;
            const timeSlot = getScheduleSlot(classInfo, today) || attendance.timeSlot || '';

            return { attendance, classInfo, timeSlot };
        })
        .filter(({ classInfo }) => isClassActiveOnDate(classInfo, today))
        .filter(({ timeSlot }) => Boolean(timeSlot))
        .filter(({ timeSlot }) => {
            const startMinutes = getStartMinutes(timeSlot);
            return startMinutes === null ? false : currentMinutes > startMinutes;
        })
        .map(({ attendance, classInfo, timeSlot }) => ({
            id: String(classInfo.id || attendance.classId),
            className: classInfo.name || attendance.className || 'Lớp học',
            subject: classSubjectName(classInfo) || attendance.subject || 'Chưa xác định môn học',
            campus: classCampusName(classInfo) || attendance.campus || 'Chưa xác định cơ sở',
            timeSlot,
            room: classInfo.room || attendance.room || 'Chưa xác định',
            href: `/attendance?date=${todayKey}&classId=${encodeURIComponent(String(attendance.classId))}&attendanceId=${encodeURIComponent(String(attendance.id))}`,
        }));

    return { missingClasses, noStudentAttendanceClasses };
}

const Dashboard = () => {
    const { user } = useAuth();
    // Vietnamese names are stored as Họ (firstName) + Tên (lastName); greet by the given name.
    const greetingTitle = `${greeting()}${user?.lastName || user?.firstName ? `, ${user?.lastName || user?.firstName}` : ''}`;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { hasAnyPermission, loading: permissionsLoading } = usePermissions();
    const [stats, setStats] = useState<DashboardStat[]>([]);
    const [error, setError] = useState('');
    const [attendanceError, setAttendanceError] = useState('');
    const [attendanceSummary, setAttendanceSummary] = useState<any>(null);
    const [attendanceAlerts, setAttendanceAlerts] = useState<DashboardAttendanceAlerts>({
        missingClasses: [],
        noStudentAttendanceClasses: [],
    });
    const [homeworkCount, setHomeworkCount] = useState<number | null>(null);
    const [manualCount, setManualCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [alertRefreshToken, setAlertRefreshToken] = useState(0);

    // A link from the bell to one alert opens its tab.
    const tabParam = searchParams.get('tab');
    const activeTab: DashboardTab = isDashboardTab(tabParam)
        ? tabParam
        : searchParams.get('homeworkAlertId') ? 'homework'
            : searchParams.get('manualAlertId') ? 'manual' : 'attendance';
    const changeTab = (tab: DashboardTab) => {
        const params = new URLSearchParams(searchParams);
        params.set('tab', tab);
        params.delete('homeworkAlertId');
        params.delete('manualAlertId');
        setSearchParams(params, { replace: true });
    };

    const canViewDashboard = hasAnyPermission([
        { resource: 'dashboard', action: 'view_general' },
        { resource: 'dashboard', action: 'view' },
    ]);
    // Head counts are for admins (whole center) and managers (their campuses) only.
    const showCounts = user?.role === 'ADMIN' || user?.role === 'MANAGER';
    const isStaff = user?.role === 'TEACHER' || user?.role === 'ASSISTANT';
    const scopeLabel = user?.role === 'ADMIN'
        ? 'Tình trạng ghi nhận trên toàn hệ thống'
        : user?.role === 'MANAGER'
            ? 'Các cơ sở bạn quản lý'
            : 'Các lớp và buổi học của bạn';

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError('');
        setAttendanceError('');

        try {
            const todayKey = dateKey(new Date());
            const [summaryResponse, attendanceResponse, classesResponse, attendanceListResponse] = await Promise.all([
                showCounts ? dashboardApi.getSummary() : Promise.resolve({ data: { data: {} } }),
                attendanceApi
                    .getStats({
                        startDate: todayKey,
                        endDate: todayKey,
                    })
                    .catch((attendanceErr: any) => {
                        setAttendanceError(attendanceErr?.message || 'Không thể tải dữ liệu điểm danh hôm nay.');
                        return { data: { data: null } };
                    }),
                classesApi.getAll().catch(() => ({ data: { data: [] } })),
                attendanceApi.getAll({ date: todayKey, ...(isStaff ? { scope: 'mine' as const } : {}) }).catch(() => ({ data: { data: [] } })),
            ]);
            const data = (summaryResponse.data as any)?.data || {};

            const nextStats: DashboardStat[] = showCounts ? [
                { label: 'Cơ sở hoạt động', value: data.campuses || 0, icon: Building2, tone: 'academy', path: '/campuses' },
                { label: 'Lớp đang hoạt động', value: data.classes || 0, icon: GraduationCap, tone: 'action', path: '/classes' },
                { label: 'Học sinh đang học', value: Number.isFinite(data.students) ? data.students : 0, icon: Users, tone: 'warning', path: '/students' },
            ] : [];

            setAttendanceSummary((attendanceResponse.data as any)?.data || null);
            setAttendanceAlerts(buildDashboardAttendanceAlerts(
                (classesResponse.data as any)?.data || [],
                (attendanceListResponse.data as any)?.data || [],
            ));
            setStats(nextStats);
        } catch (err: any) {
            setError(err?.message || 'Không thể tải dữ liệu tổng quan.');
        } finally {
            setLoading(false);
            setAlertRefreshToken((current) => current + 1);
        }
    }, [showCounts, isStaff]);

    useEffect(() => {
        if (canViewDashboard) void loadDashboard();
    }, [canViewDashboard, loadDashboard]);

    const dateLabel = new Date().toLocaleDateString('vi-VN', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    const headerActions = (
        <>
            <div className="flex h-10 items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 text-sm text-ink-600">
                <CalendarDays className="h-4 w-4" />
                <span className="hidden sm:inline">{dateLabel}</span>
                <span className="sm:hidden">Hôm nay</span>
            </div>
            <Button
                onClick={loadDashboard}
                variant="secondary"
                size="icon"
                loading={loading}
                icon={<RefreshCw className="h-4 w-4" />}
                aria-label="Làm mới tổng quan"
                title="Làm mới"
            />
        </>
    );
    const hasAttendanceData = Boolean(
        attendanceSummary &&
        ['totalStudents', 'totalPresent', 'totalAbsent', 'totalLate', 'totalExcused'].some(
            (key) => Number(attendanceSummary[key]) > 0,
        ),
    );

    if (permissionsLoading) {
        return (
            <PageContainer eyebrow="Tổng quan" title={greetingTitle} subtitle="Đây là tình hình trung tâm hôm nay.">
                <SectionPanel><LoadingState title="Đang tải dữ liệu vận hành..." /></SectionPanel>
            </PageContainer>
        );
    }

    if (!canViewDashboard) {
        return (
            <PageContainer title="Truy cập bị từ chối" subtitle="Tài khoản của bạn không có quyền xem trang tổng quan.">
                <SectionPanel className="flex min-h-72 flex-col items-center justify-center p-6 text-center">
                    <ShieldAlert className="mb-4 h-9 w-9 text-danger-600" />
                    <h2 className="text-lg font-semibold text-ink-900">Không thể mở trang tổng quan</h2>
                    <p className="mt-2 max-w-md text-sm leading-6 text-ink-600">
                        Liên hệ quản trị viên để được cấp quyền hoặc tiếp tục với công việc điểm danh.
                    </p>
                    <Button className="mt-5" onClick={() => navigate('/attendance')}>Đi tới điểm danh</Button>
                </SectionPanel>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer eyebrow="Tổng quan" title={greetingTitle} subtitle="Đây là tình hình trung tâm hôm nay." actions={headerActions}>
                <SectionPanel><ErrorState title="Tải trang tổng quan thất bại" description={error} onRetry={loadDashboard} /></SectionPanel>
            </PageContainer>
        );
    }

    if (loading) {
        return (
            <PageContainer eyebrow="Tổng quan" title={greetingTitle} subtitle="Đây là tình hình trung tâm hôm nay." actions={headerActions}>
                <SectionPanel><LoadingState title="Đang tải dữ liệu vận hành..." /></SectionPanel>
            </PageContainer>
        );
    }

    const attendanceItems = [
        { label: 'Có mặt', value: attendanceSummary?.totalPresent || 0, className: 'text-action-700' },
        { label: 'Vắng', value: attendanceSummary?.totalAbsent || 0, className: 'text-danger-700' },
        { label: 'Đi muộn', value: attendanceSummary?.totalLate || 0, className: 'text-warning-700' },
        { label: 'Có phép', value: attendanceSummary?.totalExcused || 0, className: 'text-ink-700' },
        { label: 'Tỷ lệ tham dự', value: `${attendanceSummary?.attendanceRate || 0}%`, className: 'text-academy-700' },
    ];
    const totalAttendanceAlerts = attendanceAlerts.missingClasses.length
        + attendanceAlerts.noStudentAttendanceClasses.length;
    const tabCounts: Record<DashboardTab, number | null> = {
        attendance: totalAttendanceAlerts,
        homework: homeworkCount,
        manual: manualCount,
    };
    // Every tab stays mounted, so their counts load and filters survive switching tabs.
    const panelProps = (tab: DashboardTab) => ({
        id: `dashboard-panel-${tab}`,
        role: 'tabpanel',
        hidden: activeTab !== tab,
        className: 'p-4 sm:p-5',
    });

    return (
        <PageContainer eyebrow="Tổng quan" title={greetingTitle} subtitle="Đây là tình hình trung tâm hôm nay." actions={headerActions}>
            {stats.length > 0 && (
                <section aria-label="Chỉ số chính" className="grid gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 grid-cols-1 sm:grid-cols-3">
                    {stats.map((stat) => (
                        <StatCard
                            key={stat.label}
                            label={stat.label}
                            value={stat.value}
                            detail={stat.detail}
                            onClick={stat.path ? () => navigate(stat.path) : undefined}
                        />
                    ))}
                </section>
            )}

            <SectionPanel aria-label="Việc cần xử lý" className="p-0">
                <DashboardTabs active={activeTab} counts={tabCounts} onChange={changeTab} />

                <section {...panelProps('attendance')} aria-label="Điểm danh hôm nay">
                    <div className="mb-4 flex items-center justify-between gap-3">
                        <p className="text-sm text-ink-500">{scopeLabel}</p>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/attendance')} icon={<Eye className="h-4 w-4" />}>
                            Mở trang Buổi học
                        </Button>
                    </div>
                    {attendanceError ? (
                        <ErrorState title="Không thể tải điểm danh" description={attendanceError} onRetry={loadDashboard} className="min-h-40" />
                    ) : hasAttendanceData ? (
                        <div className="mb-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 sm:grid-cols-5">
                            {attendanceItems.map((item) => (
                                <div key={item.label} className="bg-white px-4 py-3">
                                    <p className="text-xs font-medium text-ink-500">{item.label}</p>
                                    <p className={`mt-0.5 text-xl font-semibold tabular-nums ${item.className}`}>{item.value}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="mb-5 text-sm text-ink-500">Chưa có dữ liệu điểm danh hôm nay.</p>
                    )}
                    <h3 className="mb-3 text-sm font-semibold text-ink-900">Lớp cần chú ý</h3>
                    <AttendanceAttentionList alerts={attendanceAlerts} />
                </section>

                <section {...panelProps('homework')} aria-label="Cảnh báo bài tập">
                    <HomeworkAlertBoard refreshToken={alertRefreshToken} onCount={setHomeworkCount} />
                </section>
                <section {...panelProps('manual')} aria-label="Cảnh báo thủ công">
                    <ManualAlertBoard refreshToken={alertRefreshToken} onCount={setManualCount} />
                </section>
            </SectionPanel>
        </PageContainer>
    );
};

export default Dashboard;
